import { APP_BASE_HREF } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtModule } from '@auth0/angular-jwt';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing.module';
import { AuthModule } from './auth/auth.module';
import { AuthInterceptor } from './auth/interceptors/auth.interceptor';
import { MonitoringErrorHandler } from './monitoring/error.handler';
import { MonitoringService } from './monitoring/monitoring.service';
import { DOCUMENT_PROVIDERS } from './shared-providers/document-provider';
import { LOCAL_STORAGE_PROVIDERS } from './shared-providers/local-storage.providers';
import { SESSION_STORAGE_PROVIDERS } from './shared-providers/session-storage.providers';
import { WINDOW_PROVIDERS } from './shared-providers/window.providers';
import { AppConfigService } from './shared-services/services/config.service';
import { IglooInitService } from './shared-services/services/igloo-init.service';
import { ThemeModule } from './themes/themes.module';
import { CommonUtils } from './utils/common-utils';
import { HostInterceptor } from './interceptors/host.interceptor';
import { ThemeService } from 'ng2-charts';
import { FiservWebhooksModule } from './fiserv-webhooks/fiserv-webhooks.module';
import { WebUpdateService } from './shared-services/services/web-update.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppMaterialModule } from './app-material.module';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { GlobalErrorHandler } from './shared-services/services/global-error-handler.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { UniversalDeviceDetectorService } from './shared-services/services/universal-device-detector.service';
import { NgxCurrency } from '@dintecom/ngx-currency';
import { TokenHandoffInitService } from './auth/services/token-handoff-init.service';

export function loadConfigService(
    configService: AppConfigService,
    injector: Injector
): () => Promise<void> {
    return () => configService.load()
        .then(() => injector.get(TokenHandoffInitService).redeem());
}

export function initIgloo(igloo: IglooInitService): () => void {
    return () => igloo.init();
}

export function fakeTokenGetter(): string {
    return undefined;
}

@NgModule({
    providers: [
        AppConfigService,
        CommonUtils,
        MonitoringService,
        ThemeService,
        WebUpdateService,
        DOCUMENT_PROVIDERS,
        LOCAL_STORAGE_PROVIDERS,
        SESSION_STORAGE_PROVIDERS,
        WINDOW_PROVIDERS,
        { provide: APP_BASE_HREF, useValue: '/pfm' },
        { provide: APP_INITIALIZER, useFactory: loadConfigService, deps: [AppConfigService, Injector], multi: true },
        { provide: APP_INITIALIZER, useFactory: initIgloo, deps: [IglooInitService], multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HostInterceptor, multi: true },
        { provide: ErrorHandler, useClass: MonitoringErrorHandler },
        { provide: ErrorHandler, useClass: GlobalErrorHandler},
        { provide: DeviceDetectorService, useClass: UniversalDeviceDetectorService}
    ],
    declarations: [
        AppComponent
    ],
    imports: [
        AppRoutingModule,
        AppMaterialModule,
        AuthModule,
        BrowserAnimationsModule,
        BrowserModule,
        FlexLayoutModule,
        FiservWebhooksModule,
        HttpClientModule,
        JwtModule.forRoot({
            config: {
                /**
                 * 'tokenGetter' is a method that JwtHelperService requires you set up. However, the app should
                 * never get to a point where JwtHelperService has to call this method. If the tokenGetter method
                 * gets the saved token from sessionStorage, there would never be an Action dispatched to the store
                 * to save it to the store's state. Better to check sessionStorage in the AuthGuard to be able to
                 * dispatch a LoginSuccess action that will re-save the flJwtToken to the store. AuthService can
                 * then take care of clearing sessionStorage if that token is not valid anymore anyway. We are
                 * really just using JwtHelperService for its decodeToken method.
                 */
                tokenGetter: fakeTokenGetter,
                throwNoTokenError: false,
                skipWhenExpired: true
            }
        }),
        ServiceWorkerModule.register('ngsw-worker.js', {
          enabled: !isDevMode(),
          // Register the ServiceWorker as soon as the application is stable
          // or after 30 seconds (whichever comes first).
          registrationStrategy: 'registerWhenStable:30000'
        }),
        ThemeModule,
        NgxCurrency
    ],
    exports: [],
    bootstrap: [AppComponent],
    schemas: []
})
export class AppModule { }
